import type { AccountGradingRecording } from "@repo/app-types/entities/teammatePerformanceSummary";
import { MessageItem } from "@repo/ui/chat/messageItem";
import { MessageTimeline } from "@repo/ui/chat/messageTimeline";
import { UserMessageForm } from "@repo/ui/chat/userMessageForm";
import { MarkdownContent } from "@repo/ui/markdown/markdownContent";
import { renderStructuredContent } from "@repo/ui/structuredContent/structuredContentRenderer";
import { IconLayoutSidebarRightCollapse } from "@tabler/icons-react";
import { Duration } from "luxon";
import type { FC } from "react";
import { Button as AriaButton, VisuallyHidden } from "react-aria-components";
import { QuoteMatchMap } from "./gradeValidationHelpers.js";
import type { ClientChatMessageWithStructuredContent } from "./useTeamPerformanceCopilot.js";

interface TeamPerformanceCopilotProps {
  visibleMessages: readonly ClientChatMessageWithStructuredContent[];
  quoteMatches: QuoteMatchMap;
  isLoading: boolean;
  composerValue: string;
  onComposerValueChange: (value: string) => void;
  onMessageSend: (message: string) => void;
  onMessageRetry: (messageId: string) => void;
  sortedRecordings: readonly AccountGradingRecording[];
  onQuotePress: (quoteId: string) => void;
  onCollapse: () => void;
}

export const TeamPerformanceCopilot: FC<TeamPerformanceCopilotProps> = (
  props,
) => {
  const {
    visibleMessages,
    quoteMatches,
    isLoading,
    composerValue,
    onComposerValueChange,
    onMessageSend,
    onMessageRetry,
    sortedRecordings,
    onQuotePress,
  } = props;

  return (
    <div className="-m-4 flex flex-auto flex-col overflow-hidden">
      <MessageTimeline messages={visibleMessages} isLoading={isLoading}>
        {(message) => (
          <MessageItem
            role={message.role}
            // Only user messages use Markdown's prose styles; structured
            // assistant content styles its own elements.
            prose={message.role === "user"}
            isSaving={message.role === "user" && message.isSaving}
            isError={message.isError}
            onRetry={() => onMessageRetry(message.id)}
          >
            {message.role === "user" ? (
              <MarkdownContent>{message.content}</MarkdownContent>
            ) : (
              renderStructuredContent(message.structuredContent, {
                getQuoteInteraction: (quoteText) => {
                  const quote = message.quotes.find(
                    (candidate) => candidate.long === quoteText,
                  );
                  if (!quote) {
                    return undefined;
                  }

                  const firstMatch = QuoteMatchMap.findByQuote(
                    quoteMatches,
                    quote.id,
                  )?.[0];
                  if (!firstMatch) {
                    return undefined;
                  }

                  const recording = sortedRecordings.find(
                    (candidate) => candidate.id === firstMatch.recordingId,
                  );
                  const fragment =
                    recording?.transcript?.fragments[
                      firstMatch.recordingFragmentIndex
                    ];

                  return {
                    onPress: () => onQuotePress(quote.id),
                    timestamp: fragment
                      ? Duration.fromMillis(fragment.start_time).toFormat(
                          "h:mm:ss",
                        )
                      : undefined,
                  };
                },
              })
            )}
          </MessageItem>
        )}
      </MessageTimeline>
      <div className="flex items-end gap-2 px-4 py-2.5">
        <UserMessageForm
          className="flex-auto"
          isDisabled={isLoading}
          value={composerValue}
          onValueChange={onComposerValueChange}
          onMessageSend={onMessageSend}
        />

        <AriaButton className="-mr-1 size-10 p-2.5" onPress={props.onCollapse}>
          <IconLayoutSidebarRightCollapse className="size-5 shrink-0 text-muted-foreground hover:text-gray-600" />
          <VisuallyHidden>Collapse assistant</VisuallyHidden>
        </AriaButton>
      </div>
    </div>
  );
};
